import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { abstractBackgroundSoft } from './BackgroundPatterns';
import { techColors, neonGlow, terminalBorder, textGlowStrong, terminalTextStyle, terminalHeaderStyle } from './TechRetroEffects';

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const accentFor = (tone) => {
  switch (tone) {
    case 'call':
    case 'cyan':
      return techColors.featureCall;
    case 'rooms':
    case 'amber':
      return techColors.featureRooms;
    case 'e2e':
    case 'green':
      return techColors.featureE2e;
    case 'chat':
    case 'blue':
    default:
      return techColors.featureChat;
  }
};

export const HomePageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100vw;
  ${abstractBackgroundSoft}
  color: ${techColors.textPrimary};
  text-align: center;
  padding: 20px;
  padding-top: max(20px, env(safe-area-inset-top));
  padding-bottom: max(56px, calc(env(safe-area-inset-bottom) + 40px));
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
  ${terminalTextStyle}

  @media (max-width: 768px) {
    padding: 16px;
    padding-top: max(16px, env(safe-area-inset-top));
    padding-bottom: max(56px, calc(env(safe-area-inset-bottom) + 40px));
    justify-content: flex-start;
  }
`;

export const Title = styled.h1`
  font-size: 4.8em;
  color: ${techColors.neonCyan};
  margin-bottom: 0.15em;
  margin-top: -0.6em;
  letter-spacing: -0.02em;
  ${textGlowStrong(techColors.neonCyan)}
  ${terminalHeaderStyle}
  animation: ${fadeUp} 0.55s ease both;

  @media (max-width: 768px) {
    font-size: 3.4em;
    margin-top: 0.4em;
  }

  @media (max-width: 480px) {
    font-size: 3em;
    margin-top: 0.55em;
    margin-bottom: 0.12em;
  }
`;

export const Subtitle = styled.h2`
  font-size: 1.25em;
  color: ${techColors.textSecondary};
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 0.4em;
  max-width: 34rem;
  line-height: 1.45;
  ${terminalTextStyle}
  animation: ${fadeUp} 0.55s ease 0.06s both;

  @media (max-width: 768px) {
    font-size: 1.08em;
    margin-bottom: 0.8em;
  }

  @media (max-width: 480px) {
    font-size: 1em;
    margin-bottom: 0.7em;
  }
`;

export const LinksList = styled.ul`
  list-style-type: none;
  padding: 0;
  display: flex;
  gap: 20px;
  justify-content: center;
  width: 100%;
  animation: ${fadeUp} 0.55s ease 0.22s both;

  @media (max-width: 768px) {
    gap: 12px;
    flex-direction: column;
    align-items: center;
  }
`;

export const StyledLink = styled(Link)`
  color: ${techColors.neonCyan};
  font-size: 1.05em;
  text-decoration: none;
  font-weight: 600;
  ${terminalBorder(techColors.neonCyan)}
  padding: 12px 36px;
  border-radius: 4px;
  background: ${techColors.darkBgTertiary};
  margin-top: 12px;
  transition: transform 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
  letter-spacing: 0.5px;
  ${terminalHeaderStyle}
  flex-grow: 1;
  text-align: center;
  max-width: 200px;

  &:hover {
    ${neonGlow(techColors.neonCyan, 'strong')}
    background: ${techColors.darkBg};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    font-size: 1.05em;
    padding: 14px 28px;
    margin-top: 8px;
    max-width: none;
    width: min(220px, 100%);
  }
`;

export const WorkspaceLink = styled(Link)`
  color: ${techColors.neonCyan};
  font-size: 1.05em;
  text-decoration: none;
  font-weight: 600;
  ${terminalBorder(techColors.neonCyan)}
  padding: 12px 36px;
  border-radius: 4px;
  background: ${techColors.darkBgTertiary};
  margin-top: 12px;
  transition: transform 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
  letter-spacing: 0.5px;
  ${terminalHeaderStyle}

  &:hover {
    ${neonGlow(techColors.neonCyan, 'strong')}
    background: ${techColors.darkBg};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    font-size: 1.05em;
    padding: 14px 28px;
    margin-top: 8px;
    width: min(220px, 100%);
  }
`;

export const FeaturesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  justify-content: center;
  align-items: stretch;
  margin: 28px 0 18px;
  max-width: 960px;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin: 16px 0 22px;
    max-width: 520px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin: 8px 0 20px;
  }
`;

export const FeatureIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 4px;
  border: 1px solid ${({ $tone }) => accentFor($tone)};
  background: ${techColors.darkBgTertiary};
  color: ${({ $tone }) => accentFor($tone)};
  font-size: 1.35em;
  margin-bottom: 14px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  @media (max-width: 768px) {
    width: 42px;
    height: 42px;
    font-size: 1.2em;
    margin-bottom: 10px;
  }
`;

export const FeatureCard = styled.div`
  --feature-accent: ${({ $tone }) => accentFor($tone)};
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  padding: 18px 16px 16px;
  background:
    linear-gradient(160deg, rgba(255, 255, 255, 0.04) 0%, transparent 42%),
    ${techColors.darkBgSecondary};
  border: 1px solid ${techColors.borderSubtle};
  border-radius: 6px;
  overflow: hidden;
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  min-height: 168px;
  animation: ${fadeUp} 0.55s ease both;
  animation-delay: ${({ $delay }) => ($delay != null ? `${$delay}ms` : '0ms')};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--feature-accent), transparent 80%);
    opacity: 0.9;
  }

  &::after {
    content: '';
    position: absolute;
    inset: auto -20% -40% auto;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: radial-gradient(circle, var(--feature-accent) 0%, transparent 70%);
    opacity: 0.07;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-4px);
    border-color: var(--feature-accent);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.28);

    ${FeatureIcon} {
      transform: translateY(-2px) scale(1.06);
      box-shadow: 0 0 0 1px var(--feature-accent);
    }
  }

  @media (max-width: 768px) {
    padding: 14px 12px 12px;
    min-height: 148px;
  }

  @media (max-width: 480px) {
    min-height: 0;
  }
`;

export const FeatureTitle = styled.h3`
  font-size: 1.05em;
  color: ${({ $tone }) => accentFor($tone)};
  font-weight: 600;
  margin: 0 0 8px 0;
  letter-spacing: 0.02em;
  ${terminalHeaderStyle}

  @media (max-width: 768px) {
    font-size: 0.98em;
    margin-bottom: 6px;
  }
`;

export const FeatureDescription = styled.p`
  font-size: 0.78em;
  color: ${techColors.textSecondary};
  text-align: left;
  line-height: 1.45;
  margin: 0;
  ${terminalTextStyle}

  @media (max-width: 768px) {
    font-size: 0.74em;
    line-height: 1.4;
  }
`;

export const VersionBadge = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.7em;
  color: ${techColors.textSecondary};
  font-weight: 500;
  letter-spacing: 0.2px;
  z-index: 100;
  opacity: 0.85;
  text-align: center;
  ${terminalTextStyle}

  @media (max-width: 768px) {
    font-size: 0.65em;
    bottom: max(12px, env(safe-area-inset-bottom));
  }
`;
